import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, Trash2, Pencil, Check, Download, RotateCw, ZoomIn, ZoomOut, Maximize, FileText, Columns2 } from "lucide-react";
import { T } from "../lib/i18n";
import { MediaCompare } from "./MediaCompare";
import { ProjectPdfPreview } from "./pdf/ProjectPdfPreview";
import { downloadFile } from "../lib/fileDownload";

const IMG_EXT = ["png", "jpg", "jpeg", "webp", "gif", "bmp", "heic", "heif", "svg"];
const VID_EXT = ["mp4", "mov", "webm", "m4v"];
const AUD_EXT = ["mp3", "m4a", "wav", "aac", "ogg", "flac"];
const SHEET_EXT = ["xls", "xlsx", "csv"];
// 图纸 / 设计源文件 / Office：浏览器不能渲染，必须显式落成 file，
// 否则会被下面的 `image/*` 规则误判成照片（DXF 的 MIME 就是 image/vnd.dxf）。
const DOC_EXT = ["dwg", "dxf", "rvt", "skp", "ifc", "cdr", "ai", "psd", "indd", "eps",
                 "doc", "docx", "ppt", "pptx", "rtf", "txt", "zip", "rar", "7z"];
// Office 与 TXT：后端能用 LibreOffice 转成 PDF，前端据此走后端逐页渲染而不是「不支持预览」。
export const OFFICE_EXT = ["doc", "docx", "xls", "xlsx", "xlsm", "ppt", "pptx", "rtf", "txt"];
/**
 * 附件 → 预览类型：photo | video | audio | pdf | sheet | office | file。
 *
 * 2026-10-20 用户反馈「CAD / CDR 等其它格式支持吗」后修正判定顺序：
 *  ① **先按扩展名判**，再退回 MIME。原来只写 `contentType.startsWith("image/")`，
 *     而 DXF 的 MIME 恰是 `image/vnd.dxf`，会被当成照片塞进 <img>，屏幕上是一张破图；
 *  ② 图纸 / 源文件 / Office 一律走 DOC_EXT 白名单，避免以后再有别的「image/* 但其实不是位图」
 *     的 MIME 掉进照片分支；
 *  ③ office 与 pdf 分开：pdf 可直接逐页渲染，office 要先由后端转 PDF。
 */
export const mediaKind = (name = "", contentType = "") => {
  const ext = (name.split(".").pop() || "").toLowerCase();
  const ct = (contentType || "").toLowerCase();
  if (DOC_EXT.includes(ext)) return OFFICE_EXT.includes(ext) ? "office" : "file";
  if (ext === "pdf" || ct === "application/pdf") return "pdf";
  if (IMG_EXT.includes(ext)) return "photo";
  if (VID_EXT.includes(ext)) return "video";
  if (AUD_EXT.includes(ext)) return "audio";
  if (SHEET_EXT.includes(ext)) return "sheet";
  // 没有可用扩展名时才退回 MIME 判断。
  if (ct.startsWith("image/")) return "photo";
  if (ct.startsWith("video/")) return "video";
  if (ct.startsWith("audio/")) return "audio";
  if (ct === "text/csv" || ct.includes("spreadsheetml")) return "sheet";
  return "file";
};

const SHEET_ROWS = 300;   // 预览行数上限：整张大表进 DOM 会把灯箱卡死
const SHEET_COLS = 40;    // 预览列数上限

/**
 * 表格（xlsx / xls / csv）页内预览。
 *
 * ⚠️ 刻意**不**用 SheetJS 的 `sheet_to_html` + `dangerouslySetInnerHTML`：
 *  ① 单元格内容来自用户上传的文件，直接当 HTML 注入就是 XSS 通道；
 *  ② 它生成的 `<table>` 没样式，在深色灯箱里读不了。
 * 这里只取二维数组自己用 React 渲染，并限制行列数（超出只预览前 N 行/列并在角上说明）。
 * xlsx 依赖按需 `import()`，不让它阻塞灯箱首屏。
 */
function SheetView({ url, name }) {
  const [st, setSt] = useState({ loading: true, err: "", sheets: [], tab: 0 });
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const buf = await res.arrayBuffer();
        const XLSX = await import("xlsx");
        // ⚠️ CSV 必须**先按 UTF-8 解码成字符串**再解析：SheetJS 拿到 ArrayBuffer 且没有 BOM 时
        //    会按 Latin-1 之类猜编码，中文列名（项目 / 金额）会变成 `é¡¹ç®` 这种乱码
        //    （2026-10-15 真机实测踩到）。xlsx/xls 是二进制格式，走原来的 array 分支。
        const isCsv = /\.csv$/i.test(name || "");
        const wb = isCsv
          ? XLSX.read(new TextDecoder("utf-8").decode(buf), { type: "string" })
          : XLSX.read(buf, { type: "array" });
        const sheets = (wb.SheetNames || []).slice(0, 20).map((n) => {
          // raw:false → 用单元格的显示文本而不是原始值：否则日期会显示成 46297.3338 这种
          // Excel 序列号，金额的千分位 / 百分比格式也会丢。
          const aoa = XLSX.utils.sheet_to_json(wb.Sheets[n], { header: 1, blankrows: false, defval: "", raw: false });
          return {
            name: n,
            rows: aoa.length,
            cols: aoa.reduce((m, r) => Math.max(m, r.length), 0),
            data: aoa.slice(0, SHEET_ROWS).map((r) => r.slice(0, SHEET_COLS)),
          };
        });
        if (!dead) setSt({ loading: false, err: "", sheets, tab: 0 });
      } catch (e) {
        if (!dead) setSt({ loading: false, err: String(e?.message || e), sheets: [], tab: 0 });
      }
    })();
    return () => { dead = true; };
  }, [url, name]);

  const sh = st.sheets[st.tab];
  const cut = sh && (sh.rows > SHEET_ROWS || sh.cols > SHEET_COLS);
  return (
    <div className="w-full h-full flex flex-col bg-white rounded-md overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="lightbox-sheet" data-file={name}>
      {(st.loading || st.err || st.sheets.length > 1 || cut) && (
        <div className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 border-b border-slate-200 bg-slate-50 overflow-x-auto">
          {st.sheets.map((s, i) => (
            <button key={s.name} type="button" onClick={() => setSt((v) => ({ ...v, tab: i }))} title={s.name}
              className={`shrink-0 max-w-[10rem] truncate px-2 py-0.5 rounded text-[11px] font-medium ${i === st.tab ? "bg-violet-600 text-white" : "text-slate-600 hover:bg-slate-200"}`}>{s.name}</button>
          ))}
          {cut && <span className="shrink-0 ml-auto text-[10px] text-slate-400 tabular-nums">{T("仅预览前")} {SHEET_ROWS} {T("行")} / {SHEET_COLS} {T("列")}</span>}
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-auto">
        {st.loading && <p className="p-8 text-center text-slate-400 text-xs">{T("表格解析中…")}</p>}
        {!st.loading && st.err && <p className="p-8 text-center text-rose-500 text-xs">{T("表格解析失败")}：{st.err}</p>}
        {sh && (
          <table className="border-collapse text-[11px] text-slate-700">
            <tbody>
              {sh.data.map((row, r) => (
                <tr key={r} className={r === 0 ? "bg-slate-100 font-semibold" : ""}>
                  <th className="sticky left-0 bg-slate-50 text-slate-400 font-normal px-1.5 py-0.5 border border-slate-200 text-right tabular-nums">{r + 1}</th>
                  {row.map((c, i) => (
                    <td key={i} title={String(c)} className="px-1.5 py-0.5 border border-slate-200 whitespace-nowrap max-w-[16rem] truncate">{String(c)}</td>
                  ))}
                </tr>
              ))}
              {!sh.data.length && <tr><td className="px-3 py-6 text-slate-300">{T("空表")}</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
const MIN = 1, MAX = 6;
const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
const IconBtn = ({ onClick, title, tid, children, disabled }) => <button type="button" disabled={disabled} onClick={onClick} title={title} className="shrink-0 rounded-full bg-slate-800 hover:bg-slate-700 disabled:opacity-40 p-1.5" data-testid={tid}>{children}</button>;

/* 通用全屏媒体查看器：items [{id,url,thumbUrl,kind: photo|video|pdf|file,name,caption,meta}]；等比完整显示、←→ / 滑动翻页、
   照片旋转 90° / 缩放（按钮 · 滚轮 · 双击 · 双指捏合）/ 放大后拖动平移；多项时可「对比」并排看两个版本；可选 说明文字编辑 / 删除 */
export const MediaLightbox = ({ items, index, onIndex, onClose, canEdit, onDelete, onCaption, tid = "media-lightbox", api, zh = true }) => {
  const [editing, setEditing] = useState(false);
  const [cap, setCap] = useState("");
  const [tf, setTf] = useState({ s: 1, r: 0, x: 0, y: 0 });
  const [cmp, setCmp] = useState(null);
  const [mediaErr, setMediaErr] = useState("");
  const [mediaTry, setMediaTry] = useState(0);
  const touch = useRef(null);
  const drag = useRef(null);
  const many = items.length > 1;
  const cur = items[index];
  const zoomed = tf.s > 1.01;
  const prev = () => many && onIndex((index - 1 + items.length) % items.length);
  const next = () => many && onIndex((index + 1) % items.length);
  const reset = () => setTf({ s: 1, r: 0, x: 0, y: 0 });
  const zoomTo = (s) => setTf((t) => { const ns = Math.min(MAX, Math.max(MIN, s)); return ns <= 1 ? { ...t, s: 1, x: 0, y: 0 } : { ...t, s: ns }; });
  useEffect(() => { setEditing(false); reset(); setMediaErr(""); setMediaTry(0); }, [index]);
  useEffect(() => {
    const onKey = (e) => {
      if (editing) return;
      if (e.key === "Escape") { e.preventDefault(); if (cmp) setCmp(null); else onClose(); return; }
      if (cmp) return;
      else if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); next(); }
      else if (e.key === "+" || e.key === "=") zoomTo(tf.s * 1.25);
      else if (e.key === "-") zoomTo(tf.s / 1.25);
      else if (e.key.toLowerCase() === "r") setTf((t) => ({ ...t, r: (t.r + 90) % 360 }));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  if (!cur) return null;
  const photo = cur.kind === "photo";
  const onTouchStart = (e) => {
    if (e.touches.length === 2) { touch.current = { pinch: dist(e.touches), s0: tf.s }; return; }
    touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, tx: tf.x, ty: tf.y, moved: false };
  };
  const onTouchMove = (e) => {
    const t = touch.current; if (!t) return;
    if (e.touches.length === 2 && t.pinch) { e.preventDefault(); zoomTo(t.s0 * (dist(e.touches) / t.pinch)); return; }
    if (zoomed && e.touches.length === 1 && t.x !== undefined) { t.moved = true; setTf((v) => ({ ...v, x: t.tx + e.touches[0].clientX - t.x, y: t.ty + e.touches[0].clientY - t.y })); }
  };
  const onTouchEnd = (e) => {
    const t = touch.current; touch.current = null;
    if (!t || t.pinch || zoomed || t.x === undefined) return;
    const dx = e.changedTouches[0].clientX - t.x;
    if (Math.abs(dx) > 40) (dx > 0 ? prev() : next());
  };
  const onMouseDown = (e) => { if (!zoomed) return; e.preventDefault(); drag.current = { x: e.clientX, y: e.clientY, tx: tf.x, ty: tf.y }; };
  const onMouseMove = (e) => { const d = drag.current; if (!d) return; setTf((v) => ({ ...v, x: d.tx + e.clientX - d.x, y: d.ty + e.clientY - d.y })); };
  const onMouseUp = () => { drag.current = null; };
  const onWheel = (e) => { if (!photo) return; zoomTo(tf.s * (e.deltaY < 0 ? 1.15 : 1 / 1.15)); };
  const stop = (e) => e.stopPropagation();
  /* 根节点带 pointer-events-auto：Radix Dialog（如「提交反馈」弹窗）打开时会把 body 设成
     pointer-events:none 并只在自己的 Content 上恢复；本组件 createPortal 到 body、不在
     Content 里 → 不显式恢复的话整个灯箱点不动。 */
  return createPortal(
    <div className="fixed inset-0 z-[10000] pointer-events-auto bg-slate-950/95 flex flex-col select-none" onClick={onClose} onTouchStart={stop} onTouchMove={stop} onTouchEnd={stop} data-testid={tid} data-index={index} data-scale={tf.s.toFixed(2)} data-rotate={tf.r}>
      <div className="mobile-safe-topbar flex items-center gap-1.5 px-3 py-2 text-white text-xs shrink-0" onClick={(e) => e.stopPropagation()}>
        <span className="truncate min-w-0 mr-auto" data-testid={`${tid}-title`}>{cur.name}{cur.meta ? ` · ${cur.meta}` : ""}</span>
        <span className="tabular-nums text-slate-300 shrink-0 mr-1" data-testid={`${tid}-counter`}>{index + 1} / {items.length}</span>
        {photo && (<>
          <IconBtn tid={`${tid}-zoom-out`} title={T("缩小")} disabled={tf.s <= MIN} onClick={() => zoomTo(tf.s / 1.25)}><ZoomOut className="h-4 w-4" /></IconBtn>
          <span className="tabular-nums text-slate-300 w-10 text-center" data-testid={`${tid}-zoom`}>{Math.round(tf.s * 100)}%</span>
          <IconBtn tid={`${tid}-zoom-in`} title={T("放大")} disabled={tf.s >= MAX} onClick={() => zoomTo(tf.s * 1.25)}><ZoomIn className="h-4 w-4" /></IconBtn>
          <IconBtn tid={`${tid}-rotate`} title={T("旋转 90°")} onClick={() => setTf((t) => ({ ...t, r: (t.r + 90) % 360 }))}><RotateCw className="h-4 w-4" /></IconBtn>
          <IconBtn tid={`${tid}-reset`} title={T("还原")} disabled={tf.s === 1 && tf.r === 0} onClick={reset}><Maximize className="h-4 w-4" /></IconBtn>
        </>)}
        {many && <IconBtn tid={`${tid}-compare`} title={T("并排对比两个版本")} onClick={() => setCmp({ left: index, right: (index + 1) % items.length })}><Columns2 className="h-4 w-4" /></IconBtn>}
        {/* 下载按钮：前端域名与后端不同域时 `<a download>` 会被浏览器忽略（只能另开标签页），
            所以有 fileId 时统一走 blob 下载；没有 fileId 的旧调用仍保留原生链接。 */}
        {cur.fileId
          ? <button type="button" onClick={() => downloadFile(cur.fileId, cur.name || "file").catch((e) => console.warn("download failed", e))}
              className="shrink-0 rounded-full bg-slate-800 hover:bg-slate-700 p-1.5" title={T("下载")} data-testid={`${tid}-download`}><Download className="h-4 w-4" /></button>
          : <a href={cur.url} download={cur.name} className="shrink-0 rounded-full bg-slate-800 hover:bg-slate-700 p-1.5" title={T("下载")} data-testid={`${tid}-download`}><Download className="h-4 w-4" /></a>}
        <IconBtn tid={`${tid}-close`} title={T("关闭")} onClick={onClose}><X className="h-4 w-4" /></IconBtn>
      </div>
      <div className={`flex-1 min-h-0 relative flex items-center justify-center px-12 sm:px-16 overflow-hidden ${zoomed ? "cursor-grab" : ""}`} style={{ touchAction: "none" }}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp} onWheel={onWheel}>
        {many && <button type="button" onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-2 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-slate-800/80 hover:bg-slate-700 text-white flex items-center justify-center" data-testid={`${tid}-prev`}><ChevronLeft className="h-6 w-6" /></button>}
        <div className="w-full h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()} onDoubleClick={() => photo && zoomTo(zoomed ? 1 : 2.5)}>
          {photo && !mediaErr && <img key={`${cur.id}-${mediaTry}`} src={cur.url} alt={cur.caption || cur.name} draggable={false} onError={() => setMediaErr("failed")} data-testid={`${tid}-img`} className="max-h-full max-w-full object-contain rounded-md transition-transform duration-100"
            style={{ transform: `translate(${tf.x}px, ${tf.y}px) scale(${tf.s}) rotate(${tf.r}deg)` }} />}
          {cur.kind === "video" && !mediaErr && <video key={`${cur.id}-${mediaTry}`} src={cur.url} controls autoPlay onError={() => setMediaErr("failed")} className="max-h-full max-w-full rounded-md bg-black" data-testid={`${tid}-video`} />}
          {mediaErr && (photo || cur.kind === "video") && (
            <div className="rounded-2xl bg-slate-900 border border-slate-700 px-8 py-6 text-center text-white max-w-md" data-testid={`${tid}-media-error`}>
              <FileText className="h-9 w-9 mx-auto text-slate-400" />
              <p className="text-sm font-semibold mt-3">{T("图片或视频加载失败")}</p>
              <p className="text-[11px] text-slate-400 mt-1">{T("原文件仍在服务器上；请检查网络或登录状态后重试。")}</p>
              <button type="button" onClick={() => { setMediaErr(""); setMediaTry((v) => v + 1); }} data-testid={`${tid}-media-retry`}
                className="inline-flex items-center gap-1.5 mt-4 rounded-full bg-violet-600 hover:bg-violet-500 px-4 py-2 text-xs font-semibold">
                <RotateCw className="h-3.5 w-3.5" />{T("重新加载")}
              </button>
            </div>
          )}
          {/* 音频（现场录音 / 语音说明）：白名单早就允许上传 mp3/m4a/wav，但此前没有播放器，
              点开只显示「不支持预览」，用户只能下载后用本地播放器听。 */}
          {cur.kind === "audio" && (
            <div className="rounded-2xl bg-slate-900 border border-slate-700 px-8 py-6 text-center text-white w-full max-w-md" data-testid={`${tid}-audio`}>
              <FileText className="h-9 w-9 mx-auto text-slate-400" />
              <p className="text-sm font-semibold mt-3 break-all">{cur.name}</p>
              <audio key={cur.id} src={cur.url} controls autoPlay className="mt-4 w-full" data-testid={`${tid}-audio-player`} />
            </div>
          )}
          {/* Office（docx/xlsx/pptx 等）：后端用 LibreOffice 转成 PDF 再逐页渲染，
              这样设计部的方案汇报 / 合同附件也能在页内读完，不必先下载。 */}
          {cur.kind === "office" && (api && cur.fileId)
            ? <div key={cur.id} className="w-full h-full min-h-0" data-testid={`${tid}-office-pages`}><ProjectPdfPreview api={api} file={{ fileId: cur.fileId, filename: cur.name, size: cur.size }} zh={zh} convert /></div>
            : cur.kind === "office" && (
              <div className="rounded-2xl bg-slate-900 border border-slate-700 px-10 py-8 text-center text-white max-w-sm" data-testid={`${tid}-office`}>
                <FileText className="h-10 w-10 mx-auto text-slate-400" /><p className="text-sm font-semibold mt-3 break-all">{cur.name}</p>
                <p className="text-[11px] text-slate-400 mt-1">{T("Office 文档请在下载后用本地软件打开")}</p>
              </div>
            )}
          {/* 2026-10-20 FB876fb29793：大 PDF 直接塞 iframe 会让浏览器预览页变空白（录屏中 48MB
              Building Set 点开即黑屏）。优先走后端逐页渲染器，只在缺少 api/fileId 的旧调用处
              回退到浏览器内嵌预览，避免影响其它媒体灯箱。 */}
          {cur.kind === "pdf" && (api && cur.fileId)
            ? <div key={cur.id} className="w-full h-full min-h-0" data-testid={`${tid}-pdf-pages`}><ProjectPdfPreview api={api} file={{ fileId: cur.fileId, filename: cur.name, size: cur.size }} zh={zh} /></div>
            : cur.kind === "pdf" && <iframe key={cur.id} src={cur.url} title={cur.name} className="w-full h-full bg-white rounded-md border-0" data-testid={`${tid}-pdf`} />}
          {cur.kind === "sheet" && <SheetView key={cur.id} url={cur.url} name={cur.name} />}
          {cur.kind === "file" && (
            <div className="rounded-2xl bg-slate-900 border border-slate-700 px-10 py-8 text-center text-white max-w-sm" data-testid={`${tid}-file`}>
              <FileText className="h-10 w-10 mx-auto text-slate-400" /><p className="text-sm font-semibold mt-3 break-all">{cur.name}</p>
              <p className="text-[11px] text-slate-400 mt-1">{T("该文件类型不支持页内预览（如 CAD / Office）")}</p>
              {/* 跨域时原生 <a download> 会退化成「新标签打开」，照样是空白页；统一走 blob 下载。 */}
              {cur.fileId
                ? <button type="button" onClick={() => downloadFile(cur.fileId, cur.name || "file").catch((e) => console.warn("download failed", e))}
                    className="inline-flex items-center gap-1.5 mt-4 rounded-full bg-violet-600 hover:bg-violet-500 px-4 py-2 text-xs font-semibold" data-testid={`${tid}-file-download`}><Download className="h-3.5 w-3.5" />{T("下载文件")}</button>
                : <a href={cur.url} download={cur.name} className="inline-flex items-center gap-1.5 mt-4 rounded-full bg-violet-600 hover:bg-violet-500 px-4 py-2 text-xs font-semibold"><Download className="h-3.5 w-3.5" />{T("下载文件")}</a>}
            </div>
          )}
        </div>
        {many && <button type="button" onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-2 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-slate-800/80 hover:bg-slate-700 text-white flex items-center justify-center" data-testid={`${tid}-next`}><ChevronRight className="h-6 w-6" /></button>}
      </div>
      <div className="shrink-0 px-3 pt-2 pb-3 space-y-2" onClick={(e) => e.stopPropagation()}>
        {(onCaption || cur.caption || onDelete) && (
          <div className="flex items-center gap-2 text-white text-xs">
            {editing ? (<><input autoFocus value={cap} onChange={(e) => setCap(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { onCaption(cur, cap); setEditing(false); } if (e.key === "Escape") setEditing(false); }} className="flex-1 rounded px-2 py-1 text-slate-800 text-xs" data-testid={`${tid}-caption-input`} placeholder={T("说明文字")} /><button type="button" onClick={() => { onCaption(cur, cap); setEditing(false); }} data-testid={`${tid}-caption-save`}><Check className="h-4 w-4" /></button></>)
              : (<><span className="flex-1 truncate" data-testid={`${tid}-caption`}>{cur.caption || (onCaption ? T("（无说明）") : "")}</span>{canEdit && onCaption && <button type="button" onClick={() => { setCap(cur.caption || ""); setEditing(true); }} data-testid={`${tid}-caption-edit`}><Pencil className="h-4 w-4" /></button>}</>)}
            {canEdit && onDelete && <button type="button" onClick={() => onDelete(cur)} className="text-red-300 hover:text-red-200" data-testid={`${tid}-del`}><Trash2 className="h-4 w-4" /></button>}
          </div>
        )}
        {photo && <p className="text-[10px] text-slate-500">{T("滚轮 / 双击 / 双指捏合缩放 · 放大后拖动查看 · R 旋转 · ← → 翻页")}</p>}
        {many && (
          <div className="flex gap-1.5 overflow-x-auto [scrollbar-width:none] pb-0.5" data-testid={`${tid}-strip`}>
            {items.map((m, i) => (
              <button key={m.id} type="button" onClick={() => onIndex(i)} className={`shrink-0 h-12 w-12 rounded-md overflow-hidden border-2 ${i === index ? "border-violet-400" : "border-transparent opacity-60 hover:opacity-100"}`} data-testid={`${tid}-thumb-${i}`} title={m.name}>
                {m.kind === "photo" || m.thumbUrl ? <img src={m.thumbUrl || m.url} alt="" className={`h-full w-full ${m.kind === "photo" ? "object-cover" : "object-contain bg-white"}`} loading="lazy" /> : <span className="h-full w-full flex items-center justify-center bg-slate-800 text-slate-300 text-[9px] uppercase">{m.kind === "video" ? T("视频") : m.kind}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      {cmp && <MediaCompare items={items} left={cmp.left} right={cmp.right} onClose={() => setCmp(null)} tid={`${tid}-compare-view`} />}
    </div>,
    document.body,
  );
};
